import { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { Download, Mail } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import ConsentLine from "../ConsentLine";

function Slider({ label, value, min, max, step, onChange, format, testid }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm font-medium text-revo-ice">{label}</label>
        <span className="font-display text-lg font-bold text-white" data-testid={`${testid}-value`}>{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(+e.target.value)}
        data-testid={testid}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-[#2FD576]"
        style={{ accentColor: "#2FD576" }}
      />
    </div>
  );
}

export default function RoiCalculator() {
  const [devices, setDevices] = useState(250);
  const [swaps, setSwaps] = useState(6);
  const [minutes, setMinutes] = useState(4);
  const [rate, setRate] = useState(28);
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const r = useMemo(() => {
    const weeklyHours = (devices * swaps * minutes) / 60;
    const annualLabor = weeklyHours * rate * 52;
    const revoEstimate = Math.max(30000, devices * 120); // Revo Enterprise: from $2,500/mo (up to 250 devices), scaling with fleet size
    const annualSavings = Math.max(0, annualLabor - revoEstimate);
    const paybackMonths = annualSavings > 0 ? (revoEstimate / (annualSavings / 12)) : 0;
    return { annualLabor, revoEstimate, annualSavings, paybackMonths };
  }, [devices, swaps, minutes, rate]);

  const money = (n) => `$${Math.round(n).toLocaleString()}`;

  const buildPdf = () => {
    const doc = new jsPDF();
    doc.setFillColor(14, 27, 51);
    doc.rect(0, 0, 210, 40, "F");
    doc.setTextColor(47, 213, 118);
    doc.setFontSize(10);
    doc.text("CHARGEREVO ENTERPRISE", 16, 18);
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text("ROI Estimate", 16, 30);

    doc.setTextColor(20, 20, 20);
    doc.setFontSize(12);
    let y = 56;
    doc.text(`Prepared for: ${company || "Your organization"}`, 16, y);
    y += 14;
    doc.setFontSize(11);
    const rows = [
      ["Devices in fleet", `${devices}`],
      ["Battery swaps / device / week", `${swaps}`],
      ["Minutes per swap", `${minutes}`],
      ["Loaded labor rate", `$${rate}/hr`],
      ["", ""],
      ["Estimated annual labor cost", money(r.annualLabor)],
      ["Revo Enterprise estimate", money(r.revoEstimate)],
    ];
    rows.forEach(([k, v]) => {
      doc.text(k, 16, y);
      doc.text(v, 150, y);
      y += 9;
    });

    doc.setFillColor(47, 213, 118);
    doc.rect(16, y + 4, 178, 34, "F");
    doc.setTextColor(14, 27, 51);
    doc.setFontSize(12);
    doc.text("Estimated annual labor savings", 22, y + 16);
    doc.setFontSize(20);
    doc.text(money(r.annualSavings), 22, y + 30);
    doc.setFontSize(11);
    doc.text(`Payback: ${r.paybackMonths.toFixed(1)} months`, 130, y + 30);

    doc.setTextColor(120, 120, 120);
    doc.setFontSize(8);
    doc.text("Estimates are illustrative and based on the inputs provided. ChargeRevo. Patent pending.", 16, 285);
    doc.save("Revo-Enterprise-ROI.pdf");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your work email to receive the report."); return; }
    setBusy(true);
    try {
      await api.post("/business-lead", {
        company: company || "Your organization",
        email,
        devices,
        swaps_per_week: swaps,
        minutes_per_swap: minutes,
        labor_rate: rate,
        annual_labor_cost: r.annualLabor,
        revo_estimate: r.revoEstimate,
        annual_savings: r.annualSavings,
        payback_months: r.paybackMonths,
      });
      buildPdf();
      toast.success("Report downloaded and emailed to you.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-navy-deep" data-testid="poc-roi-calculator">
      <div className="grid lg:grid-cols-2">
        <div className="space-y-7 border-b border-white/10 p-7 md:p-10 lg:border-b-0 lg:border-r">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-revo-green">POC · Enterprise ROI</p>
            <h3 className="mt-2 font-display text-3xl font-extrabold text-white">Kill the battery-swap shift.</h3>
          </div>
          <Slider label="Devices in fleet" value={devices} min={10} max={2000} step={10} onChange={setDevices} format={(v) => v.toLocaleString()} testid="roi-devices" />
          <Slider label="Swaps per device / week" value={swaps} min={1} max={20} step={1} onChange={setSwaps} format={(v) => v} testid="roi-swaps" />
          <Slider label="Minutes per swap" value={minutes} min={1} max={15} step={1} onChange={setMinutes} format={(v) => `${v} min`} testid="roi-minutes" />
          <Slider label="Loaded labor rate" value={rate} min={12} max={90} step={1} onChange={setRate} format={(v) => `$${v}/hr`} testid="roi-rate" />
        </div>

        <div className="p-7 md:p-10">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Annual labor cost today" value={money(r.annualLabor)} muted testid="roi-labor-cost" />
            <Stat label="Revo Enterprise est." value={money(r.revoEstimate)} muted testid="roi-revo-cost" />
          </div>
          <div className="mt-4 rounded-2xl border border-revo-green/30 bg-revo-green/5 p-6">
            <p className="text-sm text-revo-ice">Estimated annual labor savings</p>
            <p className="mt-1 font-display text-5xl font-black text-revo-green" data-testid="roi-annual-savings">{money(r.annualSavings)}</p>
            <p className="mt-2 text-sm text-white">Payback in <span className="font-bold text-revo-green" data-testid="roi-payback">{r.paybackMonths.toFixed(1)} months</span></p>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="roi-form">
            <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company (optional)" data-testid="roi-company-input" className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-revo-ice/40 focus:border-revo-green focus:outline-none" />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email — to receive your PDF" data-testid="roi-email-input" className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-revo-ice/40 focus:border-revo-green focus:outline-none" />
            <button type="submit" disabled={busy} data-testid="roi-download-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-revo-green py-3 text-sm font-bold text-navy transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60">
              {busy ? "Preparing…" : <><Download className="h-4 w-4" /> Download PDF <Mail className="h-4 w-4" /></>}
            </button>
            <p className="text-center text-xs text-revo-ice/50">We email a copy and our team follows up with a precise quote.</p>
            <ConsentLine dark testid="roi-consent" />
          </form>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-xs text-revo-ice/60">{label}</p>
      <p className="mt-1 font-display text-xl font-extrabold text-white" data-testid={testid}>{value}</p>
    </div>
  );
}
